/**
 * Games of chance of the "shop" group (see ./shop.ts): darts (the roulette screen 'roul') and stealing in the shop
 * with the apology dialog of the shopkeeper.
 *
 * Steal flow (new_interf): 'try_steal' on the shop screen calls steal() (7575-7607), which saves and shows the
 * 'steal' screen. After a failed steal the dialog offers two apologies, each once (steal_luck / steal_merch,
 * 5295-5323, visible while !steal_try_luck / !steal_try_merch, render 3704-3716); a failed "merchant" apology also
 * uses up the "luck" one. Those flags lived only in the client's memory, so the rules keep them as server state
 * (key STEAL_APOLOGY_KEY of ServerState, shape PendingStealApology): steal.attempt writes or clears it,
 * steal.apology reads and updates it, and both return the whole new server state as `result.serverState`.
 * The dialog was modal: nothing could happen between the failed steal and the apologies. So a pending apology holds
 * only while the player state is exactly the one the failed steal left (PendingStealApology.state, a fingerprint):
 * any command that changed the state in between (a time refresh that lowered the tiredness, a perk change that
 * raised the chance, a purchase, ...) closes the dialog for good.
 */
import { argsObject, intArg, knownKey, stringArg } from '../args';
import { int, keys } from '../as3';
import type { RuleContext } from '../context';
import { RuleError } from '../errors';
import { defineOp } from '../registry';
import { encodeState, type PlayerState, type WorkingState } from '../state';
import {
  addWeaponCopy,
  atLimit,
  currentLevel,
  CURRENCIES,
  idArg,
  newCopy,
  perkLevel,
  serverStateOf,
  shopLists,
  type Currency,
  type ServerState,
} from './shop-common';

// ------------------------------------------------------------------------------------------------ roulette

/** The bets of the roul screen: controls 'bet_gold_500' ... 'bet_diam_50' (render 3242-3250). */
export const ROULETTE_BETS: Readonly<Record<Currency, readonly number[]>> = { gold: [500, 3000, 10000], diam: [5, 25, 50] };

export interface RouletteBetArgs {
  currency: Currency;
  amount: number;
}

export interface RouletteBetResult {
  currency: Currency;
  amount: number;
  /** FWP.get_roul: the prize class 1..7, 0 = nothing. */
  prizeClass: number;
  /** FWP.roul_weapon: the prize weapon ('' = nothing). */
  weapon: string;
  /** 1 when the copy was added, 0 when nothing was won or the weapon was at its limit (the prize is lost). */
  added: number;
}

// ------------------------------------------------------------------------------------------------ steal

/** The key of the pending apology in ServerState. */
export const STEAL_APOLOGY_KEY = 'stealApology';

/**
 * The apology still possible after a failed steal (server state; the original's steal_try_luck / steal_try_merch
 * flags of the open 'steal' dialog). It is valid only while the dialog could still be open in the original: the
 * player state is unchanged since the failed steal (`state`; a failed apology changes nothing) and it is still the
 * local day of the steal (a new day resets today_steals, FWP.modify_last_enter, after which forgiving 20 steals would
 * give the player extra attempts).
 */
export interface PendingStealApology {
  [field: string]: string | number | boolean;
  /** The weapon of the failed steal. */
  weapon: string;
  /** Server time of the failed steal (unix seconds). */
  at: number;
  /** ctx.calendar(at).dayIndex. */
  day: number;
  /** steal_luck ("Ой, нет, извините...") can still be tried. */
  luck: boolean;
  /** steal_merch ("А вы в курсе...") can still be tried. */
  merch: boolean;
  /** stealDialogState of the state the failed steal left: the apology needs exactly that state. */
  state: string;
}

export type ApologyKind = 'luck' | 'merch';
export const APOLOGY_KINDS: readonly ApologyKind[] = ['luck', 'merch'];

/** The pending apology of a server state, if any (malformed entries count as none). */
export function pendingApology(serverState: ServerState): PendingStealApology | null {
  const v = serverState[STEAL_APOLOGY_KEY];
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const a = v as Record<string, unknown>;
  if (typeof a.weapon !== 'string' || typeof a.at !== 'number' || typeof a.day !== 'number' || typeof a.luck !== 'boolean' || typeof a.merch !== 'boolean' || typeof a.state !== 'string') return null;
  return { weapon: a.weapon, at: a.at, day: a.day, luck: a.luck, merch: a.merch, state: a.state };
}

/** cyrb53: a 53-bit string hash (14 hex digits). Not cryptographic; the player cannot choose the states it hashes. */
function hash53(text: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 2654435761);
    h2 = Math.imul(h2 ^ c, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16).padStart(14, '0');
}

/**
 * Fingerprint of the player as stored (encodeState): equal for the state a failed steal returned and the same state
 * decoded again for the next op (encodeState(decodeState(s)) is byte-identical to s), different after any change.
 */
export function stealDialogState(state: WorkingState): string {
  return hash53(JSON.stringify(encodeState(state).player));
}

export interface StealAttemptResult {
  id: string;
  success: boolean;
  /** The chance shown to the player (return_steal_prob - 1 must beat true_random(0, 100)). */
  prob: number;
  /** The apologies the dialog offers now (null after a successful steal). */
  apology: { luck: boolean; merch: boolean } | null;
  /** The whole new server state for the backend to store. */
  serverState: ServerState;
}

export interface StealApologyResult {
  kind: ApologyKind;
  success: boolean;
  prob: number;
  /** The apologies still possible (null when the dialog ends: success or nothing left). */
  apology: { luck: boolean; merch: boolean } | null;
  serverState: ServerState;
}

/** FWP.get_steal_prob (FWP.ts:6051-6070). */
export function getStealProb(playerLevel: unknown, weaponLevel: unknown, steal: unknown, pe: unknown): number {
  const l = Number(playerLevel);
  const w = Number(weaponLevel);
  const s = Number(steal);
  const e = Number(pe);
  let lv = l;
  if (l > 70) lv = 70;
  let prob = 0;
  const k = (s + e / 3) / 11;
  prob = (lv / 70) * 2 + 20 * Math.pow((70 - w) / 70, 4) * (k + 0.5);
  prob = Math.ceil(prob) - 1;
  if (prob < 0) prob = 0;
  return Number(prob);
}

/** new_interf.return_steal_prob (new_interf.ts:7438-7451). */
export function stealProb(p: PlayerState, weaponDef: any, defs: RuleContext['defs']): number {
  return Number(getStealProb(currentLevel(p, defs), weaponDef.level, perkLevel(p, 'steal'), perkLevel(p, 'pe')));
}

/** new_interf.return_steal_luck_prob (new_interf.ts:7453-7470): round(education * 10 + luck * 2 + luck2 * 3). */
export function stealLuckProb(p: PlayerState): number {
  return Math.round(perkLevel(p, 'education') * 10 + perkLevel(p, 'luck') * 2 + perkLevel(p, 'luck2') * 3);
}

/** new_interf.return_steal_merch_prob (new_interf.ts:7472-7484): round((merchant + bar) * 8). */
export function stealMerchProb(p: PlayerState): number {
  return Math.round((perkLevel(p, 'merchant') + perkLevel(p, 'bar')) * 8);
}

function withApology(serverState: ServerState, a: PendingStealApology | null): ServerState {
  const out: ServerState = { ...serverState };
  if (a && (a.luck || a.merch)) out[STEAL_APOLOGY_KEY] = a;
  else delete out[STEAL_APOLOGY_KEY];
  return out;
}

export const chanceOps = {
  /**
   * roulette.bet { currency, amount }: 'bet_<currency>_<amount>' (new_interf 6164-6219).
   * Preconditions: one of the six bets of the screen, and the balance covers it (the handler's own check).
   * Mutations: the bet; true_random(0, 99) against the cumulative ROUL_VER['<currency>_<amount>'] gives the prize
   * class (0 = nothing); for a class, true_random(1, 200) repeats until a weapon with that id (not 61, no 'rez' in the
   * key) has that class; the copy is added unless the weapon is at its limit (then the prize is lost - no limit
   * precondition); achiev.wins += added; task 11 -> 12. No weapon_optimisation (as in the original).
   */
  'roulette.bet': defineOp<RouletteBetArgs, RouletteBetResult>({
    parseArgs(raw) {
      const o = argsObject(raw, ['currency', 'amount']);
      const currency = stringArg(o, 'currency', { oneOf: CURRENCIES });
      const amount = intArg(o, 'amount', { min: 1 });
      if (!ROULETTE_BETS[currency].includes(amount)) throw new RuleError('bad_args', { field: 'amount', problem: `must be one of ${ROULETTE_BETS[currency].join(', ')}` });
      return { currency, amount };
    },
    run(state, { currency, amount }, ctx) {
      const p = state.player;
      const W = ctx.defs.WEAPONS_DEF;
      if (!(p[currency] >= amount)) throw new RuleError(currency == 'gold' ? 'not_enough_gold' : 'not_enough_diam', { have: p[currency], need: amount });
      const ver = ctx.defs.ROUL_VER[currency + '_' + String(amount)];
      // The draw loop below only ends when the class has a weapon (true for every class of the tables); guard against
      // an endless loop with a real random source should the tables ever change.
      const candidates = (cls: number) => keys(W).some((k) => W[k as string].id >= 1 && W[k as string].id <= 200 && W[k as string].id != 61 && String(k).search('rez') == -1 && W[k as string].wpclass == cls);

      p[currency] = int(p[currency] - amount);
      const cum: number[] = [ver[0], 0];
      for (let i = 1; i <= 7; i++) cum[i] = ver[i] + cum[i - 1];
      const r = ctx.rng.int(0, 99);
      let prizeClass = 0;
      let cls = 0;
      while (cls <= 7) {
        if (r < cum[cls]) {
          prizeClass = cls;
          break;
        }
        cls++;
      }
      let weapon = '';
      let added = 0;
      if (prizeClass != 0) {
        if (!candidates(cls)) throw new Error(`roulette.bet: no weapon of class ${cls} can be won`);
        do {
          const n = ctx.rng.int(1, 200);
          for (const k of keys(W)) {
            const d = W[k as string];
            if (d.id == n && d.id != 61 && String(k).search('rez') == -1) {
              if (d.wpclass == cls) weapon = String(k);
            }
          }
        } while (weapon == '');
        const w = p.weapons;
        if (Boolean(w[weapon]) && w[weapon][0] < W[weapon].limit) {
          ++w[weapon][0];
          w[weapon][w[weapon][0]] = newCopy();
          added++;
        } else if (!w[weapon]) {
          w[weapon] = [1, newCopy()];
          added++;
        }
      }
      if (p.achiev['wins']) p.achiev['wins'] += added;
      else p.achiev['wins'] = added;
      if (p.cur_task == 11) p.cur_task = 12;
      return { state, result: { currency, amount, prizeClass, weapon, added } };
    },
  }),

  /**
   * steal.attempt { id }: 'try_steal' on the shop screen -> new_interf.steal() (7575-7607).
   * Preconditions: the shop lets the player in (today_steals < 8, init('shop') 673-684; after every steal the game
   * leaves the shop and re-enters through that check), the weapon is listed (shopLists) with a price (level != 0) and
   * not at its limit (try_steal visibility, render 2379-2400). The player's level does not matter.
   * Mutations: today_steals + 1, last_steal = last_enter; true_random(0, 100) < return_steal_prob - 1 wins a stolen
   * copy (no weapon_optimisation), task 24 -> 25, achiev.steal++; otherwise tired += 100 and today_steals += 20, and
   * the server state records the pending apology (both kinds available).
   */
  'steal.attempt': defineOp<{ id: string }, StealAttemptResult>({
    parseArgs(raw) {
      const o = argsObject(raw, ['id']);
      return { id: idArg(o) };
    },
    run(state, { id }, ctx) {
      const p = state.player;
      const W = ctx.defs.WEAPONS_DEF;
      knownKey(W, id);
      if (Number(p.today_steals) >= 8) throw new RuleError('shop_closed', { todaySteals: p.today_steals });
      const def: any = W[id];
      if (!shopLists('weapons', id, def) || !(def.id > 0) || def.level == 0) throw new RuleError('not_available', { id });
      if (atLimit('weapons', p.weapons, id, def)) throw new RuleError('limit_reached', { id, limit: def.limit });
      const serverState = serverStateOf(ctx);

      ++p.today_steals;
      p.last_steal = p.last_enter;
      const prob = stealProb(p, def, ctx.defs);
      const success = prob - 1 > ctx.rng.int(0, 100);
      let pending: PendingStealApology | null = null;
      if (success) {
        addWeaponCopy(p.weapons, id, newCopy(true));
        if (p.cur_task == 24) p.cur_task = 25;
        if (p.achiev['steal']) ++p.achiev['steal'];
        else p.achiev['steal'] = 1;
      } else {
        p.tired += 100;
        p.today_steals += 20;
        // The last mutation of the op: the fingerprint is that of the state the backend stores.
        pending = { weapon: id, at: ctx.now, day: ctx.calendar(ctx.now).dayIndex, luck: true, merch: true, state: stealDialogState(state) };
      }
      return {
        state,
        result: { id, success, prob, apology: pending ? { luck: true, merch: true } : null, serverState: withApology(serverState, pending) },
      };
    },
  }),

  /**
   * steal.apology { kind: luck|merch }: 'steal_luck' / 'steal_merch' of the steal dialog (new_interf 5295-5323).
   * Preconditions: a pending apology from a failed steal of today with the state unchanged since then (server state;
   * the dialog was modal), and this kind not tried yet.
   * Mutations: return_steal_luck_prob / return_steal_merch_prob > true_random(0, 100) forgives the penalty
   * (today_steals -= 20, tired -= 100) and ends the dialog; a failure only uses the attempt ('luck' uses up luck,
   * 'merch' uses up both, as steal_merch sets steal_try_luck too) and saves nothing in the original.
   */
  'steal.apology': defineOp<{ kind: ApologyKind }, StealApologyResult>({
    parseArgs(raw) {
      const o = argsObject(raw, ['kind']);
      return { kind: stringArg(o, 'kind', { oneOf: APOLOGY_KINDS }) };
    },
    run(state, { kind }, ctx) {
      const p = state.player;
      const serverState = serverStateOf(ctx);
      const pending = pendingApology(serverState);
      if (!pending || pending.day !== ctx.calendar(ctx.now).dayIndex || pending.at > ctx.now) throw new RuleError('no_pending_steal', {});
      // Another command changed the state after the failed steal: the dialog is closed in the original.
      if (pending.state !== stealDialogState(state)) throw new RuleError('no_pending_steal', { problem: 'state_changed' });
      if (!pending[kind]) throw new RuleError('already_tried', { kind });

      const prob = kind == 'luck' ? stealLuckProb(p) : stealMerchProb(p);
      const success = prob > ctx.rng.int(0, 100);
      let next: PendingStealApology | null;
      if (success) {
        p.today_steals -= 20;
        p.tired -= 100;
        next = null;
      } else if (kind == 'luck') {
        next = { ...pending, luck: false };
      } else {
        next = { ...pending, luck: false, merch: false };
      }
      const out = withApology(serverState, next);
      const left = pendingApology(out);
      return { state, result: { kind, success, prob, apology: left ? { luck: left.luck, merch: left.merch } : null, serverState: out } };
    },
  }),
};
